import React, { useEffect } from "react";
import ConnectedField from "components/ConnectedField";
import { getFieldPath, integerTransformer } from "utils/form";
import Checkbox from "rc-checkbox";
import { useViewport } from "utils/context";
import { useFormikContext } from "formik";
import get from "lodash/get";

/**
 * Primaries can have their adjustable_quantity set
 * Which will be picked and set within the server side getSessionLineItems
 * quantity: 1,
 * adjustable_quantity: {
 *  enabled: Joi.boolean().default(false),
 *  minimum: Joi.number().min(0).default(0),
 *  maximum: Joi.number().min(1).default(99)
 * };
 */

const MIN_QUANTITY = 1;
const MIN_ADJUSTABLE_QUANTITY = 0;
const ADJUSTABLE_QUANTITY = "adjustable_quantity";
const QUANTITY = "quantity";
const ADJUST_MAX_LIMIT = 999;

const DEFAULT_ADJUSTABLE_QUANTITY = {
  enabled: true,
  minimum: MIN_ADJUSTABLE_QUANTITY
};

const QuantityControl = ({ name, disabled }) => {
  const { setFieldValue, values } = useFormikContext();
  const adjustableConfigPath = getFieldPath(name, ADJUSTABLE_QUANTITY);
  const enabledPath = `${adjustableConfigPath}.enabled`;
  const enabledValue = get(values, enabledPath);
  const adjustable = enabledValue && typeof enabledValue === "boolean";
  const quantityPath = getFieldPath(name, QUANTITY);
  const { isSmall } = useViewport();
  const minPath = `${adjustableConfigPath}.minimum`;
  const maxPath = `${adjustableConfigPath}.maximum`;
  const minValue = Number(get(values, minPath));
  const minIsNumber = !Number.isNaN(minValue);
  const maxValue = Number(get(values, maxPath));
  const maxIsNumber = !Number.isNaN(maxValue);
  const rangeAreNumbers = maxIsNumber && minIsNumber;

  /**
   * Adjust the max up if the min exceeds it
   */
  useEffect(() => {
    if (rangeAreNumbers && minValue > maxValue) {
      setFieldValue(maxPath, minValue);
    }
  }, [rangeAreNumbers, minValue]);

  const toggleProps = {
    disabled,
    icons: false,
    checked: adjustable,
    onChange: (evt) => {
      const { checked } = evt.target;

      if (checked) {
        setFieldValue(adjustableConfigPath, DEFAULT_ADJUSTABLE_QUANTITY);
      } else {
        setFieldValue(adjustableConfigPath, null);
      }
      /**
       * When toggling we always set quantity to be 1
       */
      setFieldValue(quantityPath, MIN_QUANTITY);
    }
  };
  const label = isSmall
    ? `Adjustable quantity`
    : `Let customers adjust quantity`;

  let quantityControls;
  if (adjustable) {
    quantityControls = (
      <div className="w-auto pl2 ml2 bl hairline-1 flex flex-row items-center">
        <div className="dib">
          <div className="f7 pr1 fw5">Min</div>
          <ConnectedField
            disabled={disabled}
            customClasses={{
              input:
                "f5 f6-ns border-box input-reset ba pa2 pa1-ns db w3 br2 lh-copy b--black-20"
            }}
            name={minPath}
            transformer={integerTransformer}
          />
        </div>
        <div className="dib pl1">
          <div className="f7 pr1 fw5">Max</div>
          <ConnectedField
            disabled={disabled}
            customClasses={{
              input:
                "f5 f6-ns border-box input-reset ba pa2 pa1-ns db w3 br2 lh-copy b--black-20"
            }}
            name={maxPath}
            transformer={(value) => {
              const updatedMax = integerTransformer(value);
              const updatedMaxResult =
                updatedMax < minValue && !Number.isNaN(minValue)
                  ? minValue
                  : updatedMax;

              return updatedMaxResult > ADJUST_MAX_LIMIT
                ? ADJUST_MAX_LIMIT
                : updatedMaxResult;
            }}
          />
        </div>
      </div>
    );
  } else {
    quantityControls = (
      <div className="w-auto pl2 ml2 bl hairline-1 flex flex-column items-start">
        <div className="f7 pr1 fw5">Qty</div>
        <ConnectedField
          disabled={disabled}
          customClasses={{
            input:
              "f5 f6-ns border-box input-reset ba pa2 pa1-ns db w3 br2 lh-copy b--black-20"
          }}
          name={`${name}.quantity`}
          transformer={integerTransformer}
        />
      </div>
    );
  }

  return (
    <div className="w-100 flex flex-row space-between">
      <div className="w-100 flex flex-row items-center">
        <Checkbox {...toggleProps} />
        <div className="f6 pl2">{label}</div>
      </div>
      {quantityControls}
    </div>
  );
};

export default QuantityControl;
